import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, Inbox, ListChecks } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

export default function AdminDashboardPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState("subscribers");
  const [subs, setSubs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem("mee_token")) { nav("/admin/login"); return; }
    Promise.all([
      api.get("/admin/subscribers"),
      api.get("/admin/contacts"),
      api.get("/admin/waitlist"),
    ]).then(([s, c, w]) => { setSubs(s.data); setContacts(c.data); setWaitlist(w.data); })
      .catch((err) => { if (err.response?.status === 401) { localStorage.removeItem("mee_token"); nav("/admin/login"); } })
      .finally(() => setLoading(false));
  }, [nav]);

  const logout = () => { localStorage.removeItem("mee_token"); nav("/admin/login"); };
  const fmt = (iso) => new Date(iso).toLocaleString();

  return (
    <div data-testid="admin-dashboard" className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
      <Seo title="Admin Dashboard" robots="noindex, nofollow" />
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="mono-label">Admin dashboard</p>
          <h1 className="mt-2 font-display text-3xl tracking-tight">Submissions</h1>
        </div>
        <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 border border-border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      <div className="mt-8 grid grid-cols-3 gap-px bg-border border border-border max-w-xl">
        <div className="bg-surface p-5"><span className="font-mono text-3xl text-primary">{subs.length}</span><p className="mt-1 mono-label">Greenpapers Subscribers</p></div>
        <div className="bg-surface p-5"><span className="font-mono text-3xl text-primary">{contacts.length}</span><p className="mt-1 mono-label">Contacts</p></div>
        <div className="bg-surface p-5"><span className="font-mono text-3xl text-primary">{waitlist.length}</span><p className="mt-1 mono-label">Waitlist</p></div>
      </div>

      <div className="mt-10 flex gap-1 border-b border-border">
        <button data-testid="tab-subscribers" onClick={() => setTab("subscribers")} className={`px-4 py-3 font-mono text-[11px] uppercase tracking-widest border-b-2 transition-colors ${tab === "subscribers" ? "border-primary text-primary" : "border-transparent text-foreground/60"}`}><Mail className="inline h-3.5 w-3.5 mr-2" />Greenpapers Subscribers</button>
        <button data-testid="tab-contacts" onClick={() => setTab("contacts")} className={`px-4 py-3 font-mono text-[11px] uppercase tracking-widest border-b-2 transition-colors ${tab === "contacts" ? "border-primary text-primary" : "border-transparent text-foreground/60"}`}><Inbox className="inline h-3.5 w-3.5 mr-2" />Contacts</button>
        <button data-testid="tab-waitlist" onClick={() => setTab("waitlist")} className={`px-4 py-3 font-mono text-[11px] uppercase tracking-widest border-b-2 transition-colors ${tab === "waitlist" ? "border-primary text-primary" : "border-transparent text-foreground/60"}`}><ListChecks className="inline h-3.5 w-3.5 mr-2" />Waitlist</button>
      </div>

      {loading ? <p className="mt-8 font-mono text-sm text-muted-foreground">Loading…</p> : (
        <div className="mt-6 border border-border overflow-x-auto">
          {tab === "subscribers" && (
            <table className="w-full text-sm" data-testid="subscribers-table">
              <thead className="bg-surface-elevated"><tr className="text-left mono-label">
                <th className="px-4 py-3 font-normal">First name</th><th className="px-4 py-3 font-normal">Email</th><th className="px-4 py-3 font-normal">Date</th>
              </tr></thead>
              <tbody>
                {subs.length === 0 ? <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">No Greenpapers subscribers yet.</td></tr> :
                  subs.map((s) => (<tr key={s.id} className="border-t border-border hover:bg-surface/50">
                    <td className="px-4 py-3">{s.first_name}</td><td className="px-4 py-3 text-foreground/70">{s.email}</td><td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmt(s.created_at)}</td>
                  </tr>))}
              </tbody>
            </table>
          )}
          {tab === "contacts" && (
            <table className="w-full text-sm" data-testid="contacts-table">
              <thead className="bg-surface-elevated"><tr className="text-left mono-label">
                <th className="px-4 py-3 font-normal">Name</th><th className="px-4 py-3 font-normal">Reason</th><th className="px-4 py-3 font-normal">Email</th><th className="px-4 py-3 font-normal">Details</th><th className="px-4 py-3 font-normal">Date</th>
              </tr></thead>
              <tbody>
                {contacts.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No inquiries yet.</td></tr> :
                  contacts.map((c) => (<tr key={c.id} className="border-t border-border hover:bg-surface/50 align-top">
                    <td className="px-4 py-3">{c.full_name}{c.organization && <div className="text-xs text-muted-foreground">{c.organization}</div>}</td>
                    <td className="px-4 py-3 text-foreground/70">{c.reason}</td>
                    <td className="px-4 py-3 text-foreground/70">{c.email}</td>
                    <td className="px-4 py-3 text-foreground/60 max-w-xs">{c.details}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{fmt(c.created_at)}</td>
                  </tr>))}
              </tbody>
            </table>
          )}
          {tab === "waitlist" && (
            <table className="w-full text-sm" data-testid="waitlist-table">
              <thead className="bg-surface-elevated"><tr className="text-left mono-label">
                <th className="px-4 py-3 font-normal">Name</th><th className="px-4 py-3 font-normal">Product</th><th className="px-4 py-3 font-normal">Email</th><th className="px-4 py-3 font-normal">Date</th>
              </tr></thead>
              <tbody>
                {waitlist.length === 0 ? <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No waitlist signups yet.</td></tr> :
                  waitlist.map((w) => (<tr key={w.id} className="border-t border-border hover:bg-surface/50">
                    <td className="px-4 py-3">{w.full_name}</td><td className="px-4 py-3 text-foreground/70">{w.product_name}</td><td className="px-4 py-3 text-foreground/70">{w.email}</td><td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmt(w.created_at)}</td>
                  </tr>))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
